import dayjs from 'dayjs';
import { PaginatedResponse } from '@libs/models/common';
import { SeriesRotationsItem, ShiftType } from '@libs/models/shifts';
import { request } from '../request';

type ShiftListRequestParams = {
  externalLocationNames?: string[];
  qualificationIds?: string[];
  startDate?: string;
  endDate?: string;
  timeZone: string;
  staffIds?: string[];
  specialityIds?: string[];
  facilityTenantIds?: string[];
  shiftTimeTypes?: ShiftType[];
  search?: string;
};

export async function fetchSeriesRotationsList(
  page = 1,
  pageSize = 10,
  filters?: ShiftListRequestParams,
): Promise<PaginatedResponse<SeriesRotationsItem[]>> {
  const response = await request.get<PaginatedResponse<SeriesRotationsListResponse>>(
    '/v2/shifts/series-and-rotations/list-view',
    {
      params: {
        pageNumber: page,
        pageSize,

        startDate: filters?.startDate ? dayjs(filters?.startDate).toServerDateTimeFormat() : null,
        endDate: filters?.endDate ? dayjs(filters?.endDate).toServerDateTimeFormat() : null,
        timeZone: filters?.timeZone,
        location_in: filters?.externalLocationNames,
        qualification_in: filters?.qualificationIds,
        staff_in: filters?.staffIds,
        specialities_in: filters?.specialityIds,
        facilities_in: filters?.facilityTenantIds,
        shifttimetypes_in: filters?.shiftTimeTypes,
        search: filters?.search,
      },
    },
  );
  return {
    ...response.data,
    items: response.data.items.map((data) => ({
      ...data,
      startDate: dayjs(data.startDate.dateTime),
      lastDate: dayjs(data.lastDate.dateTime),
      timeZone: data.startDate.timeZone,
      recurrence: data.recurrenceType
        ? (data.recurrenceType.toLowerCase() as 'series' | 'rotation')
        : data.id.match('shiftseries')
          ? 'series'
          : 'rotation',
      recurrenceId: data.id,
      qualifications: data.qualificationToPayFilters,
      specialty: data.specialityToPay?.title || undefined,
      notes: data.note ? [data.note] : [],
      assignees:
        data.staff?.map((s) => ({
          id: s.id,
          firstName: s.firstName,
          lastName: s.lastName,
          name: [s.firstName, s.lastName].filter(Boolean).join(' '),
          avatar: s.imageUrl,
        })) ?? [],
      location: {
        name: data.location.name,
        city: data.location.city,
        state: data.location.state,
        address1: data.location.firstAddress,
        address2: data.location.secondAddress,
      },
      allowedMultipleAssignees: data.canHasMultipleAssignee,
      canAssign: data.canAssign,
      isArchived: !!data.isArchive,
      isShared: !!data.isShared,
      isUrgent: !!data.isUrgent,
      tags: Array.from(new Set(data.tags || [])),
    })),
  };
}

type SeriesRotationsListResponse = {
  id: string;
  externalId: string;
  assignRequest: {
    status: 'Pending' | 'Approved' | 'Declined';
    staff: {
      id: string; // 'staffmember-00000000000000000000000000';
      firstName: string;
      lastName: string;
      fullName: string;
      imageUrl: string;
    };
    declineReason: string;
  } | null;
  startDate: {
    dateTime: string;
    timeZone: string;
  };
  lastDate: {
    dateTime: string;
    timeZone: string;
  };
  qualificationToPayFilters: {
    id: string;
    name: string;
    abbreviation: string;
    category: string;
  }[];
  chosenQualifications: {
    id: string;
    name: string;
    abbreviation: string;
    category: string;
  }[];
  specialityToPay: {
    id: string | null;
    title: string | null;
    category: string | null;
  } | null;
  note: string;
  unassignReason: string | null;
  staff:
    | {
        id: string;
        firstName: string;
        lastName: string;
        imageUrl: string;
      }[]
    | null;
  location: {
    city: string;
    state: string;
    name: string;
    firstAddress: string;
    secondAddress: string;
  };
  facilityTenantId: string | null;
  canHasMultipleAssignee: boolean;
  canAssign: boolean;
  totalShiftCount: number;
  assignedShiftCount: number;
  isArchive: boolean;
  isShared: boolean;
  isUrgent: boolean;
  tags: string[];
  recurrenceType: 'Series' | 'Rotation';
}[];
